import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "./api/api";
import styles from "./ParentResult.module.css";

import MapView from "./map/MapView";
import SearchResult from "./components/SearchResult";
import PoiInfoPanel from "./components/PoiInfoPanel";
import { modules } from "./modules/registry";
import { ModuleContext } from "./modules/ModuleContext";

import { normalizePOO, normalizeToMapPoints } from "./services/mapAdapter";
import { cachedLoad } from "./modules/cache";


export default function ParentResult() {
  // no tab open at first: the sub-buttons (categories, filters) of a tab only
  // appear once it is clicked, so they don't push the map down
  const [topic, setTopic] = useState(null);

  const [languages, setLanguages] = useState([]);
  const [language, setLanguage] = useState("de");

  // active topic tabs (key + label), configured in the DB (topic/topic_i18n)
  const [activeTopics, setActiveTopics] = useState([]);

  // map content published by the active module (see ModuleContext.js)
  const [results, setResults] = useState([]);
  const [routes, setRoutes] = useState([]);

  const [poos, setPoos] = useState([]);
  const [selectedPoo, setSelectedPoo] = useState(null);

  // overlay layers toggled independently of point results (e.g. Zielgruppe -> Fahrrad-Routen)
  const [activeOverlays, setActiveOverlays] = useState({});

  const [isLoading, setIsLoading] = useState(false);

  // side column next to the map that modules render their result list into
  // (see modules/SideList.js); a callback ref so modules re-render once it exists
  const [sideSlot, setSideSlot] = useState(null);
  const [hasSideList, setHasSideList] = useState(false);

  // point the map should jump to (new object each time, so re-clicks re-focus)
  const [mapFocus, setMapFocus] = useState(null);
  const focusMap = useCallback((point) => setMapFocus({ ...point }), []);

  // opened via a QR code (/qr/:slug): centre the map on that location
  const { slug } = useParams();

  // QR location map: all POIs of all categories, shown while no module has
  // put its own points on the map (see MapView locations below)
  const [qrPoints, setQrPoints] = useState([]);

  // tabs shown to the user: DB-configured topics once loaded, falling back to
  // the local registry (unordered) so the bar isn't empty before the fetch resolves
  const tabs = activeTopics.length
    ? activeTopics.filter((t) => modules[t.key])
    : Object.keys(modules).map((key) => ({ key, label: modules[key].fallbackLabel }));

  const ModulePanel = topic ? modules[topic]?.Panel : null;

  // ----------------------------
  // available languages
  // ----------------------------
  useEffect(() => {
    const load = async () => {
      const data = await api.language();
      setLanguages(data);
    };

    load();
  }, []);

  // ----------------------------
  // QR code location — unknown slug: just the normal map
  // ----------------------------
  useEffect(() => {
    if (!slug) return;

    let cancelled = false;

    api
      .qr(slug)
      .then((qr) => {
        if (cancelled) return;
        setMapFocus({
          latitude: qr.latitude,
          longitude: qr.longitude,
          zoom: qr.zoom_stufe,
          youAreHere: qr.location,
        });
      })
      .catch((err) => console.warn("QR location not found:", slug, err));

    return () => {
      cancelled = true;
    };
  }, [slug]);

  // ----------------------------
  // QR code location: everything around it — POIs of every category (same
  // cache keys as PoiPanel, so opening a category later needs no request)
  // ----------------------------
  useEffect(() => {
    if (!slug) return;

    let cancelled = false;

    const load = async () => {
      const categories = await api.category(language);
      const perCategory = await Promise.all(
        categories.map((cat) =>
          cachedLoad(`poi-${cat.category_id}-${language}`, async () => {
            const data = await api.poi(cat.category_id, language);
            return normalizeToMapPoints(data, "PointOfInterest");
          }),
        ),
      );
      if (!cancelled) setQrPoints(perCategory.flat());
    };

    load().catch((err) => console.error("QR POI load failed:", err));

    return () => {
      cancelled = true;
    };
  }, [slug, language]);

  // ----------------------------
  // active topics (tabs) — which ones, in what order, with what label
  // ----------------------------
  useEffect(() => {
    const load = async () => {
      const data = await api.topic(language);
      setActiveTopics(data);
    };

    load();
  }, [language]);

useEffect(() => {
  async function loadPOO() {
    const data = await api.poo(language);

    console.log("RAW POO:", data);

    const normalized = normalizePOO(data);

    console.log("NORMALIZED POO:", normalized);

    setPoos(normalized);
  }

  loadPOO();

}, [language]);

  // ----------------------------
  // OVERLAY TOGGLE (e.g. Zielgruppe -> Fahrrad-Routen)
  // ----------------------------
  const handleToggleOverlay = useCallback((key) => {
    setActiveOverlays((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  }, []);

  // ----------------------------
  // TOPIC SWITCH — the old module unmounts (losing its own state, e.g. the
  // selected category); clear whatever it put on the map. Overlays stay.
  // ----------------------------
  const handleTopicChange = (newTopic) => {
    // clicking the open tab again closes it (hides its sub-buttons)
    setTopic((current) => (current === newTopic ? null : newTopic));
    setResults([]);
    setRoutes([]);
    setIsLoading(false);
  };

  // services handed to the active module panel
  const moduleContext = useMemo(
    () => ({
      language,
      showPoints: setResults,
      showRoutes: setRoutes,
      activeOverlays,
      toggleOverlay: handleToggleOverlay,
      selectItem: setSelectedPoo,
      selectedItem: selectedPoo,
      focusMap,
      sideSlot,
      setHasSideList,
      setLoading: setIsLoading,
    }),
    [language, activeOverlays, handleToggleOverlay, selectedPoo, focusMap, sideSlot],
  );





  return (
    <div>


      {/* ---------------- TOP BAR: TOPIC TABS + LANGUAGE SELECT ---------------- */}
      <div className={styles.topBar}>
        <div className={styles.topicTabs}>
          {tabs.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => handleTopicChange(key)}
              className={
                topic === key
                  ? `${styles.topicButton} ${styles.topicButtonActive}`
                  : styles.topicButton
              }
            >
              {label}
            </button>
          ))}
        </div>

        <select
          className={styles.languageSelect}
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
        >
          {languages.map((l) => (
            <option key={l.code} value={l.code}>
              {l.name}
            </option>
          ))}
        </select>
      </div>
      {/* ---------------- MODULE UI ---------------- */}
      {ModulePanel && (
        <ModuleContext.Provider value={moduleContext}>
          <ModulePanel key={topic} />
        </ModuleContext.Provider>
      )}
      {/* ---------------- MAP + SELECTED POI PANEL ---------------- */}
      <div className={styles.mapWithPanel}>
        <div className={styles.mapColumn}>
          <MapView
            locations={results.length ? results : qrPoints}
            autoFit={results.length > 0}
            orientationPoints={poos}
            routes={routes}
            activeOverlays={activeOverlays}
            onSelectOrientationPoint={setSelectedPoo}
            onSelectLocation={setSelectedPoo}
            focus={mapFocus}
          />
        </div>

        <PoiInfoPanel
          point={selectedPoo}
          onClose={() => setSelectedPoo(null)}
          backLabel={hasSideList ? (language === "de" ? "← Zurück" : "← Back") : null}
        />

        {/* module result list (Veranstaltungen, Gastronomie, …); on desktop
            the detail panel takes its place while open, on mobile the list
            stays below the map and the detail opens as a bottom sheet */}
        <div
          ref={setSideSlot}
          className={
            selectedPoo
              ? `${styles.sideList} ${styles.sideListBehindDetail}`
              : styles.sideList
          }
        />
      </div>



      {/* ---------------- SEARCH LIST ---------------- */}
      <SearchResult
        locations={results}
        isLoading={isLoading}
      />

    </div>
  );
}
